import { useLocale, useLogger } from "@amzn/xb-hooks";
import { useCallback, useEffect, useRef, useState } from "react";
import { useCookieRegistry } from "./useCookieRegistry";

import { CookieConfig } from "@amzn/aws-tc-experience-bridge-xps-registry";
import { AWSCShortbread } from "@amzn/shortbread";
import { notifyConsentUpdated, setShortbreadClient } from "../stores/ShortbreadStore";
import { useShortbreadDomain } from "./useShortbreadDomain";

/**
 * Remove either the shortbread created by s_code (wrong ui) or
 * remove an existing shortbread so we can adjust the language.
 * {@see https://w.amazon.com/bin/view/Shortbread/usage/} under
 * the heading "How can I change languages on shortbread without a page reload".
 *
 * NOTE: Should only be called within a useEffect (direct dom manipulation)
 */
function removeShortbread() {
  const existingShortbreadEl = document.getElementById("awsccc-sb-ux-c");
  existingShortbreadEl && existingShortbreadEl.remove();
}

/**
 * Initialize the shortbread client.
 *
 * This hook is intended to be used as a singleton in the root of the app, but there is no mechanism
 * currently enforcing that. However, limited testing demonstrates that it is idempotent-ish™.
 */
export const useInitShortbread = () => {
  const domain = useShortbreadDomain();
  const { locale, isLoading } = useLocale();
  const { debug } = useLogger();
  const registry = useRef<CookieConfig>(useCookieRegistry()).current;
  const [isReady, setIsReady] = useState(false);
  const timeoutId = useRef<ReturnType<typeof setTimeout>>();

  // Initialize the shortbread client
  useEffect(() => {
    const listener = () => {
      timeoutId.current = setTimeout(() => {
        debug("ready to initialize shortbread");
        setIsReady(true);
      }, 10);
    };

    // Handles race condition. See this comment for further details.
    // https://w.amazon.com/bin/view/Shortbread/documentation/adobeanalytics/?viewer=comments#xwikicomment_0
    if (document.readyState !== "loading") {
      listener();
    } else {
      document.addEventListener("DOMContentLoaded", listener);
    }
    return () => {
      clearTimeout(timeoutId.current);
      document.removeEventListener("DOMContentLoaded", listener);
    };
  }, [debug]);

  const initializeShortbread = useCallback(() => {
    const client = AWSCShortbread({
      domain,
      language: locale.shortbreadLanguage,
      registry,
      onConsentChanged: notifyConsentUpdated,
    });
    client.checkForCookieConsent(); // idempotent (https://tiny.amazon.com/in7jo84d/check-for-consent)
    setShortbreadClient(client);
  }, [locale, registry]);

  useEffect(() => {
    if (isReady && !isLoading) {
      debug("(re)creating shortbread client");
      removeShortbread();
      initializeShortbread();
    } else {
      debug("skipping refreshing of shortbread prior to initialization");
    }
  }, [initializeShortbread, isLoading, isReady, debug]);
};
